<template>
  <b-modal
    :visible="visible"
    :title="$t('involvedModal.add.title')"
    size="lg"
    centered
    hide-footer
    no-close-on-backdrop
    @update:visible="$emit('update:visible', $event)"
    @hidden="onHidden"
    @shown="onShown"
  >
    <!-- 1. Natureza -->
    <div class="form-group">
      <label class="font-weight-bold mb-1">{{ $t('involvedModal.add.nature.label') }}</label>
      <div
        v-if="loadingTypes"
        class="text-muted small"
      >
        <b-spinner small /> {{ $t('involvedModal.add.nature.loading') }}
      </div>
      <div
        v-else
        class="btn-group-toggle d-flex flex-wrap"
        style="gap: 8px"
      >
        <button
          v-for="t in selectableTypes"
          :key="t.code"
          type="button"
          class="btn btn-sm"
          :class="entityType === t.code ? 'btn-primary' : 'btn-outline-secondary'"
          @click="selectType(t.code)"
        >
          <feather-icon
            v-if="t.icon"
            :icon="t.icon"
            size="14"
            class="mr-25"
          />
          {{ t.label }}
        </button>
      </div>
    </div>

    <!-- 2. Papel -->
    <div class="form-group">
      <label class="font-weight-bold mb-1">{{ $t('involvedModal.add.role.label') }}</label>
      <select
        v-model="roleId"
        class="form-control"
        :disabled="loadingRoles"
      >
        <option :value="null">
          {{ $t('involvedModal.add.role.placeholder') }}
        </option>
        <option
          v-for="r in roles"
          :key="r.id"
          :value="r.id"
        >
          {{ r.label }}
        </option>
      </select>
      <small
        v-if="!loadingRoles && roles.length === 0"
        class="text-muted"
      >
        {{ $t('involvedModal.add.role.empty') }}
      </small>
    </div>

    <hr>

    <!-- Indicador de cadastro existente vinculado (preenchido automaticamente) -->
    <div
      v-if="reuse"
      class="alert alert-success py-1 px-2 d-flex justify-content-between align-items-center"
    >
      <span class="small">
        <feather-icon
          icon="CheckCircleIcon"
          size="14"
          class="mr-25"
        />
        {{ $t('involvedModal.add.reuse.linked') }} <strong>{{ reuse.display_label }}</strong>
      </span>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        @click="clearReuse"
      >
        {{ $t('involvedModal.add.reuse.clear') }}
      </button>
    </div>
    <div
      v-else-if="lookupLoading"
      class="text-muted small mb-2"
    >
      <b-spinner small /> {{ $t('involvedModal.add.reuse.searching') }}
    </div>

    <component
      :is="formComponent"
      v-model="activeForm"
      :errors="errors"
      :disabled="!!reuse"
      :contact-editable="entityType === 'person'"
      @lookup-key="onKeyLookup"
    />

    <div class="d-flex justify-content-between align-items-center mt-2">
      <button
        type="button"
        class="btn btn-outline-secondary"
        @click="$emit('update:visible', false)"
      >
        {{ $t('involvedModal.add.buttons.close') }}
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="saving"
        @click="submit"
      >
        <b-spinner
          v-if="saving"
          small
          class="mr-25"
        />
        <feather-icon
          v-else
          icon="PlusIcon"
          size="14"
          class="mr-25"
        />
        {{ $t('involvedModal.add.buttons.submit') }}
      </button>
    </div>
  </b-modal>
</template>

<script>
import { BModal, BSpinner } from 'bootstrap-vue'
import InvolvedPersonForm from './InvolvedPersonForm.vue'
import InvolvedVehicleForm from './InvolvedVehicleForm.vue'
import InvolvedObjectForm from './InvolvedObjectForm.vue'
import InvolvedPropertyForm from './InvolvedPropertyForm.vue'
import InvolvedPhoneForm from './InvolvedPhoneForm.vue'

const FORM_COMPONENTS = {
  person: 'InvolvedPersonForm',
  vehicle: 'InvolvedVehicleForm',
  object: 'InvolvedObjectForm',
  property: 'InvolvedPropertyForm',
  phone: 'InvolvedPhoneForm',
}

const LOOKUP_FIELDS = {
  person: { field: 'cpf' },
  vehicle: { field: 'plate' },
  phone: { field: 'imei' },
}

function emptyPerson() {
  return {
    unidentified: false,
    name: '',
    apparent_description: '',
    age: null,
    age_unit: 'anos',
    gender: '',
    social_name: '',
    cpf: '',
    rg: '',
    cns: '',
    birth_date: '',
    race: '',
    telephone: '',
    cellphone: '',
  }
}
function emptyVehicle() {
  return {
    unidentified: false,
    plate: '',
    vehicle_type: '',
    color: '',
    brand: '',
    model: '',
    year: '',
    renavam: '',
    chassis: '',
    apparent_description: '',
    observation: '',
  }
}
function emptyObject() {
  return {
    object_type: '',
    description: '',
    brand: '',
    model: '',
    quantity: 1,
    serial_number: '',
    estimated_value: null,
    observation: '',
  }
}
function emptyProperty() {
  return {
    property_type: '', address: '', reference: '', description: '', observation: '',
  }
}
function emptyPhone() {
  return {
    number: '', imei: '', carrier: '', owner_name: '', observation: '',
  }
}

export default {
  name: 'OccurrenceInvolvedAddModal',
  components: {
    BModal,
    BSpinner,
    InvolvedPersonForm,
    InvolvedVehicleForm,
    InvolvedObjectForm,
    InvolvedPropertyForm,
    InvolvedPhoneForm,
  },
  props: {
    visible: { type: Boolean, default: false },
    occurrenceId: { type: [Number, String], required: true },
    disciplineId: { type: [Number, String], default: null },
  },
  data() {
    return {
      loadingTypes: false,
      entityTypes: [],
      entityType: 'person',

      loadingRoles: false,
      roles: [],
      roleId: null,

      forms: {
        person: emptyPerson(),
        vehicle: emptyVehicle(),
        object: emptyObject(),
        property: emptyProperty(),
        phone: emptyPhone(),
      },
      errors: {},

      lookupLoading: false,
      reuse: null,

      saving: false,
    }
  },
  computed: {
    // Só naturezas com formulário implementado (person/vehicle/object/property/phone).
    selectableTypes() {
      return this.entityTypes.filter(t => FORM_COMPONENTS[t.code])
    },
    formComponent() {
      return FORM_COMPONENTS[this.entityType] || 'InvolvedPersonForm'
    },
    lookupField() {
      const base = LOOKUP_FIELDS[this.entityType]
      if (!base) return null
      const labels = {
        cpf: {
          label: this.$t('involvedModal.add.lookup.cpf.label'),
          placeholder: this.$t('involvedModal.add.lookup.cpf.placeholder'),
        },
        plate: {
          label: this.$t('involvedModal.add.lookup.plate.label'),
          placeholder: this.$t('involvedModal.add.lookup.plate.placeholder'),
        },
        imei: {
          label: this.$t('involvedModal.add.lookup.imei.label'),
          placeholder: this.$t('involvedModal.add.lookup.imei.placeholder'),
        },
      }
      return { ...base, ...labels[base.field] }
    },
    activeForm: {
      get() {
        return this.forms[this.entityType] || this.forms.person
      },
      set(v) {
        this.$set(this.forms, this.entityType, v)
      },
    },
  },
  methods: {
    async onShown() {
      await this.loadEntityTypes()
      await this.loadRoles()
    },
    onHidden() {
      this.resetAll()
      this.$emit('close')
    },
    resetAll() {
      this.entityType = 'person'
      this.roleId = null
      this.forms = {
        person: emptyPerson(),
        vehicle: emptyVehicle(),
        object: emptyObject(),
        property: emptyProperty(),
        phone: emptyPhone(),
      }
      this.errors = {}
      this.clearLookup()
    },
    clearLookup() {
      this.lookupLoading = false
      this.reuse = null
    },
    emptyFormFor(code) {
      const factories = {
        person: emptyPerson,
        vehicle: emptyVehicle,
        object: emptyObject,
        property: emptyProperty,
        phone: emptyPhone,
      }
      return (factories[code] || emptyPerson)()
    },
    clearReuse() {
      this.reuse = null
      this.$set(this.forms, this.entityType, this.emptyFormFor(this.entityType))
    },
    async selectType(code) {
      if (this.entityType === code) return
      this.entityType = code
      this.roleId = null
      this.errors = {}
      this.clearLookup()
      await this.loadRoles()
    },
    async loadEntityTypes() {
      this.loadingTypes = true
      try {
        const params = this.disciplineId ? { discipline_id: this.disciplineId } : {}
        const res = await this.$http.get(`${this.$store.state.api}involved_entity_types`, { params })
        this.entityTypes = res.data.data || res.data || []
      } catch (e) {
        this.entityTypes = [{ code: 'person', label: this.$t('involvedModal.add.nature.person'), icon: 'UserIcon' }]
      } finally {
        this.loadingTypes = false
      }
    },
    async loadRoles() {
      this.loadingRoles = true
      this.roles = []
      try {
        const params = { entity_type: this.entityType }
        if (this.disciplineId) params.discipline_id = this.disciplineId
        const res = await this.$http.get(`${this.$store.state.api}involved_roles`, { params })
        this.roles = res.data.data || res.data || []
        // Pré-seleciona se houver um único papel.
        if (this.roles.length === 1) this.roleId = this.roles[0].id
      } catch (e) {
        this.roles = []
      } finally {
        this.loadingRoles = false
      }
    },
    // Busca automática ao sair do campo-chave (CPF/placa/IMEI) do formulário.
    async onKeyLookup(value) {
      const key = (value || '').toString().trim()
      if (!key || !this.lookupField || this.reuse) return
      this.lookupLoading = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}involved/lookup`, {
          params: { entity_type: this.entityType, key, key_type: this.lookupField.field },
        })
        const found = res.data.data || null
        if (found) {
          this.fillFormFromLookup(found)
          this.reuse = found
        }
      } catch (e) {
        // silencioso: se a busca falhar, segue como cadastro novo
      } finally {
        this.lookupLoading = false
      }
    },
    fillFormFromLookup(found) {
      const skip = ['entity_type', 'entity_id', 'display_label']
      const form = this.forms[this.entityType]
      Object.keys(found).forEach(k => {
        if (!skip.includes(k) && k in form) this.$set(form, k, found[k])
      })
    },
    validate() {
      this.errors = {}
      // Papel é obrigatório quando há papéis disponíveis para a natureza.
      if (this.roles.length > 0 && !this.roleId) {
        this.$message(this.$t('involvedModal.add.messages.warningTitle'), this.$t('involvedModal.add.messages.selectRoleRequired'), 'warning')
        return false
      }
      if (this.reuse) return true
      if (this.entityType === 'person') {
        const f = this.forms.person
        const nameField = f.unidentified ? f.apparent_description : f.name
        if (!nameField || !nameField.trim()) {
          this.$set(this.errors, 'name', true)
          return false
        }
      }
      if (this.entityType === 'object') {
        const d = this.forms.object.description
        if (!d || !d.trim()) {
          this.$set(this.errors, 'description', true)
          return false
        }
      }
      return true
    },
    async submit() {
      if (!this.validate()) return

      const payload = {
        occurrence_id: this.occurrenceId,
        entity_type: this.entityType,
        involved_role_id: this.roleId,
      }
      if (this.reuse) {
        payload.entity_id = this.reuse.entity_id
        // Telefone/celular continuam editáveis mesmo com cadastro reaproveitado —
        // manda só esses dois pro backend aplicar em cima do cadastro existente.
        if (this.entityType === 'person') {
          payload.entity = {
            telephone: this.forms.person.telephone,
            cellphone: this.forms.person.cellphone,
          }
        }
      } else {
        payload.entity = this.activeForm
      }

      this.saving = true
      try {
        await this.$http.post(`${this.$store.state.api}occurrence_involveds`, payload)
        this.$message(this.$t('involvedModal.add.messages.successTitle'), this.$t('involvedModal.add.messages.added'), 'success')
        this.$emit('added')
        this.$emit('update:visible', false)
      } catch (e) {
        const msg = (e.response && e.response.data) || this.$t('involvedModal.add.messages.addError')
        this.$message(this.$t('involvedModal.add.messages.errorTitle'), typeof msg === 'string' ? msg : this.$t('involvedModal.add.messages.addError'), 'error')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
